import { createContext, useCallback, useContext, useState } from 'react'
import type { ReactNode } from 'react'
import { TIMEFRAMES } from '../lib/timeframe'
import type { Timeframe } from '../lib/timeframe'

const STORAGE_KEY = 'global-timeframe'

interface TimeframeContextValue {
  timeframe: Timeframe
  setTimeframe: (tf: Timeframe) => void
}

const TimeframeContext = createContext<TimeframeContextValue | null>(null)

function readStored(): Timeframe {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    if (v && (TIMEFRAMES as string[]).includes(v)) return v as Timeframe
  } catch {
    /* storage unavailable — fall through to default */
  }
  return 'DAY'
}

export function TimeframeProvider({ children }: { children: ReactNode }) {
  const [timeframe, setState] = useState<Timeframe>(readStored)
  const setTimeframe = useCallback((tf: Timeframe) => {
    setState(tf)
    try {
      localStorage.setItem(STORAGE_KEY, tf)
    } catch {
      /* non-fatal */
    }
  }, [])
  return (
    <TimeframeContext.Provider value={{ timeframe, setTimeframe }}>
      {children}
    </TimeframeContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTimeframe(): TimeframeContextValue {
  const ctx = useContext(TimeframeContext)
  if (!ctx) throw new Error('useTimeframe must be used inside <TimeframeProvider>')
  return ctx
}
